import React, { useState, useEffect, useMemo } from "react";
import classNames from "classnames";
import { Container, Row, Col } from "react-bootstrap";
import { Formik, Form } from "formik";
import getDisplayFields from "@app/lib/hooks/getDisplayFields";
import getYupSchema from "@app/lib/hooks/getYupSchema";
import FormBuilderElements from "@app/components/FormBuilder/FormBuilderElements";
import PageWrapper from "@app/components/PageWrapper";
import Fieldset from "@app/components/Fieldset";
import Separator from "@app/components/Separator";
import * as styles from "./FormBuilder.module.scss";

export const getDefaultValue = (field) => {
	switch (field.elementType) {
		case "attributeSelector":
			return field?.useSingle ? "" : [];
		case "extendedPropertyList":
			return [];
		case "subFormPage":
			return [];
		case "repeatableList":
			return [getInitialValues(field?.formGroup?.elements)];
		case "datepicker":
			return new Date();
		case "select":
			return field.multiselect === true ? [] : "";
		default:
			return "";
	}
}; // TODO set defaults for all types

export const getInitialValues = (fields) => {
	return fields.reduce(
		(acc, field) => ({
			...acc,
			[field.name]: field.initialValue || getDefaultValue(field),
		}),
		{},
	);
};

export const filterFollowUp = (isFollowUp, element) => {
	if (!isFollowUp) return element?.elementType !== "followUp";
	return true;
};

const useFormikPropsBuilder = (config) => {
	const { values, fields } = config;
	const currentValues = { ...getInitialValues(fields), ...values };
	const displayFields = getDisplayFields(fields, currentValues);
	const validationSchema = getYupSchema(displayFields);

	return {
		initialValues: currentValues,
		displayFields,
		validationSchema,
		enableReinitialize: !true,
	};
};

const FormBuilder = ({
	values,
	fieldsets,
	header,
	subheader,
	onChange,
	onSubmit,
	isFollowUp,
	isOverlay = false,
	isSubPage = false,
	Navigation,
	observers = [],
	observerProps,
	instantErrors = [],
	finalSeparator = true,
}) => {
	const FormBuilderClassName = classNames({
		[styles["form-builder"]]: true,
	});
	let [currentFieldsets, setCurrentFieldsets] = useState(fieldsets);
	const updateFieldsets = (newFieldsets) => {
		currentFieldsets = newFieldsets;
		setCurrentFieldsets(newFieldsets);
	};
	const formikFields = useMemo(() => {
		return currentFieldsets?.flatMap((fieldset) => {
			return fieldset.elements
				? fieldset.elements.filter((element) => element.name !== "" && element.name !== undefined)
				: [];
		});
	}, [currentFieldsets]);

	const configProps = useFormikPropsBuilder({ fields: formikFields, values });
	const instantErrorFields = instantErrors?.map(({ path }) => path);

	return (
		<Formik
			{...configProps}
			onSubmit={async (values, { setSubmitting }) => {
				if (typeof onSubmit === "function") {
					setSubmitting(true);
					await onSubmit(values);
					setSubmitting(false);
				}
			}}
			validateOnMount={true}
		>
			{(formikProps) => {
				const { values, errors, setErrors } = formikProps;

				useEffect(() => {
					onChange?.({ formikProps, currentFieldsets, setCurrentFieldsets, formikFields });
				}, [values]);

				return (
					<Form className={FormBuilderClassName}>
						{observers.map((Observer, i) => (
							<Observer
								key={i}
								currentFieldsets={currentFieldsets}
								setCurrentFieldsets={updateFieldsets}
								{...observerProps}
							/>
						))}
						<PageWrapper valign={"center"} noStyling={isOverlay || isSubPage}>
							<Container className={isOverlay ? "p-0" : ""}>
								<Row>
									{(header || subheader) && (
										<Col xs={12}>
											{header && <h1 className={"mb-0"}>{header}</h1>}
											{subheader && <p className={"text-small text-dim"}>{subheader}</p>}
										</Col>
									)}

									{currentFieldsets.map((fieldset, index) => {
										const displayFields = getDisplayFields(
											fieldset.elements.filter((element) => filterFollowUp(isFollowUp, element)),
											values,
										);
										return displayFields.length > 0 ? (
											<Col key={index} xs={12}>
												<Fieldset
													noStyling={!isSubPage}
													highlighted={fieldset.highlighted}
													tabsContainer={fieldset.tabsContainer}
													layout={fieldset.layout}
												>
													<Row>
														{(fieldset.headline || fieldset.subline) && (
															<Col xs={12}>
																{fieldset.headline && (
																	<h1 className={"mb-0"}>{fieldset.headline}</h1>
																)}
																{fieldset.subline && (
																	<p className={"text-small text-dim"}>
																		{fieldset.subline}
																	</p>
																)}
															</Col>
														)}

														{fieldset.elements && (
															<FormBuilderElements
																key={index}
																values={values}
																isFollowUp={isFollowUp}
																displayFields={displayFields}
																instantErrorFields={instantErrorFields}
																instantErrors={instantErrors}
															/>
														)}
													</Row>
													{!isSubPage && !isOverlay && finalSeparator && <Separator />}
												</Fieldset>
											</Col>
										) : null;
									})}
									{Navigation && <Navigation formikProps={formikProps} formikFields={formikFields} />}
								</Row>
							</Container>
						</PageWrapper>
					</Form>
				);
			}}
		</Formik>
	);
};

FormBuilder.propTypes = {};

export default FormBuilder;
